import EasyStar from 'easystarjs';

const COLLISION_LANE_DEPTH = 48;

export class CollisionMap extends Phaser.Events.EventEmitter {
  constructor(scene) {
    super();
    this._scene = scene;
    this._collisionGridSize = 16;

    // create collision grid
    this._collisionGrid = [];
    const stepsX = Math.ceil(this._scene._map.mapWidth / this._collisionGridSize) + 5; // buffer of 5 tiles to ensure hero can walk of screen at exit
    const stepsY = Math.ceil(this._scene._map.mapHeight / this._collisionGridSize);
    for (let y = 0; y < stepsY; y++) {
      if (!this._collisionGrid[y]) this._collisionGrid.push([]);
      for (let x = 0; x < stepsX; x++) {
        this._collisionGrid[y][x] = 0;
      }
    }

    // initliaze easy star for pathfinding
    this._easystar = new EasyStar.js();
    this._easystar.setGrid(this._collisionGrid);
    this._easystar.setAcceptableTiles(0);
    this._easystar.enableDiagonals();

    this._worldBodies = [];
    this._worldObjects = [];
    this._worldTriggers = [];

    this._collisionColliders = [];
    this._collisionCollidersQueued = [];
  }

  // =============================================================================================
  // MAIN UPDATE LOPP
  // =============================================================================================

  update() {
    // update pathfinder
    this._easystar.calculate();
    // update colliders
    this.updateCollisionColliders();
    // update collision grid if needed
    if (this._dirty) {
      this.updateCollisionGrid();
      this._renderDebug(true);
    } else this._renderDebug();
  }

  // =============================================================================================
  // MANAGE COLLISION COLLIDERS
  // =============================================================================================

  updateCollisionColliders() {
    if (this._collisionCollidersQueued.length > 0) {
      this._collisionColliders.push(...this._collisionCollidersQueued);
      this._collisionCollidersQueued = [];
    }
    let colliderA, colliderB, collision;
    // loop through colliders in backwards order ( for spliceing and secondry loop )
    for (let c, i = this._collisionColliders.length - 1; i >= 0; i--) {
      // cache main collider
      colliderA = this._collisionColliders[i];

      if (colliderA.dirty) {
        colliderA.dirty = false;
        this._dirty = true;
      }
      // if the collider is destroyed, clear it out
      if (colliderA.destroyed) {
        colliderA.clearCollisionColliders();
        this._collisionColliders.splice(i, 1);
        this._dirty = true;
      }
      // otherwise if it's enabled, run collision checks
      if (colliderA.enabled) {
        colliderA.checkCurrentCollisionColliders();

        if (i > 0) {
          // loop through all other colliders up to the main collider index, anything after would have been checked
          for (c = 0; c < i; c++) {
            // cache second collider
            colliderB = this._collisionColliders[c];
            // check collider depeths are in the same lane and overlap
            collision =
              Math.abs(colliderA.depth - colliderB.depth) <= COLLISION_LANE_DEPTH &&
              colliderA.overlapsCollider(colliderB);

            if (collision) {
              // add collisions if they collide
              colliderA.addCollisionCollider(colliderB);
              colliderB.addCollisionCollider(colliderA);
            }
          }
        }
      } else colliderA.clearCollisionColliders();
    }
  }

  // called by CollisionCollider to self register when created
  _registerCollisionCollider(collider) {
    this._collisionCollidersQueued.push(collider);
    if (collider.solid) this._dirty = true;
  }

  // =============================================================================================
  // MANAGE COLLISION GRID
  // =============================================================================================

  updateCollisionGrid() {
    if (!this._dirty) return;
    for (let y = 0; y < this._collisionGrid.length; y++) {
      for (let x = 0; x < this._collisionGrid[y].length; x++) {
        this._collisionGrid[y][x] = 0;
      }
    }
    for (let collider, sPos, ePos, i = 0; i < this._collisionColliders.length; i++) {
      collider = this._collisionColliders[i];
      if (!collider.enabled || !collider.solid) continue;

      sPos = this.worldToGrid(collider.x, collider.y);
      ePos = this.worldToGrid(collider.x + collider.width, collider.y + collider.height);

      for (let y = sPos.y; y <= ePos.y; y++) {
        for (let x = sPos.x; x <= ePos.x; x++) {
          this._collisionGrid[y][x] = 1;
        }
      }
    }
    this._dirty = false;
  }

  // =============================================================================================
  // TRIGGERS
  // =============================================================================================

  addWorldTrigger(object) {
    if (!this._worldTriggers.includes(object)) {
      this._worldTriggers.push(object);
    }
  }

  removeWorldTrigger(object) {
    const index = this._worldTriggers.indexOf(object);
    if (index > -1) {
      this._worldTriggers.splice(index, 1);
    }
  }

  checkWorldTriggers(rect) {
    for (let i = 0; i < this._worldTriggers.length; i++) {
      const trigger = this._worldTriggers[i];
      if (Phaser.Geom.Rectangle.Overlaps(trigger.colliderCollision, rect)) {
        trigger.trigger();
      }
    }
  }

  // =============================================================================================
  // FIND PATH
  // =============================================================================================

  findPath(from, to, callback, callbackScope) {
    const translatedFrom = this.worldToGrid(from.x, from.y);
    const translatedTo = this.worldToGrid(to.x, to.y);
    this._easystar.findPath(
      translatedFrom.x,
      translatedFrom.y,
      translatedTo.x,
      translatedTo.y,
      (path) => {
        if (path && path[0].x === translatedFrom.x && path[0].y === translatedFrom.y) {
          path.shift();
        }

        if (path) {
          // const shrinkResult = [path[0]];
          // let currentDeltaX = Math.sign(path[1].x - path[0].x);
          // let currentDeltaY = Math.sign(path[1].y - path[0].y);
          // for (let i = 1; i < path.length - 1; i++) {
          //   const newDeltaX = Math.sign(path[i + 1].x - path[i].x);
          //   const newDeltaY = Math.sign(path[i + 1].y - path[i].y);
          //   if (newDeltaX != currentDeltaX || newDeltaY != currentDeltaY) {
          //     shrinkResult.push(path[i]);
          //     currentDeltaX = newDeltaX;
          //     currentDeltaY = newDeltaY;
          //   }
          // }

          // shrinkResult.push(path[path.length - 1]);
          callback.call(
            callbackScope,
            path.map((p) => this.gridToWorld(p.x, p.y, true))
          );
        } else {
          callback.call(callbackScope, false);
        }
      }
    );
  }

  // =============================================================================================
  // COORDINATE TRANSLATION
  // =============================================================================================

  gridToWorld(x, y, centered) {
    return {
      x: x * this._collisionGridSize + (centered ? this._collisionGridSize * 0.5 : 0),
      y: y * this._collisionGridSize + (centered ? this._collisionGridSize * 0.5 : 0),
    };
  }

  worldToGrid(x, y) {
    return {
      x: Math.min(this._collisionGrid[0].length - 1, Math.floor(x / this._collisionGridSize)),
      y: Math.min(this._collisionGrid.length - 1, Math.floor(y / this._collisionGridSize)),
    };
  }

  isWorldWalkable(x, y) {
    const pos = this.worldToGrid(x, y);
    return pos ? this._collisionGrid[pos.y][pos.x] === 0 : false;
  }

  canMoveVertical(worldX, worldY, direction) {
    const remainder = worldY % this._collisionGridSize;
    if (remainder > this._collisionGridSize * 0.5 && direction < 0) direction = 0;
    if (remainder < this._collisionGridSize * 0.5 && direction > 0) direction = 0;

    const pos = this.worldToGrid(worldX, worldY);
    return this.isValidGridPos(pos.x, pos.y + direction)
      ? this._collisionGrid[pos.y + direction][pos.x] === 0
      : false;
  }

  canMoveHorizontal(worldX, worldY, direction) {
    const pos = this.worldToGrid(worldX, worldY);
    return this.isValidGridPos(pos.x + direction, pos.y)
      ? this._collisionGrid[pos.y][pos.x + direction] === 0
      : false;
  }

  isValidGridPos(x, y) {
    return y >= 0 && y < this._collisionGrid.length && x >= 0 && x < this._collisionGrid[y].length;
  }

  // =============================================================================================
  // GET TILES
  // =============================================================================================

  getTilesInWorldRect(rect, walkableOnly) {
    const min = this.worldToGrid(rect.x, rect.y);
    const max = this.worldToGrid(rect.x + rect.width, rect.y + rect.height);
    const results = [];
    for (let y = min.y - 1; y <= max.y; y++) {
      if (!this._collisionGrid[y]) continue;
      for (let x = min.x - 1; x <= max.x; x++) {
        if (walkableOnly && this._collisionGrid[y][x] === 0) {
          results.push(this.gridToWorld(x, y, true));
        } else if (!walkableOnly) {
          results.push(this.gridToWorld(x, y, true));
        }
      }
    }
    return results;
  }

  // =============================================================================================
  // DEBUG COLLISION GRID
  // =============================================================================================

  _renderDebug(dirty) {
    if (this._scene.game.model.showDebugCollisionColliders) {
      this._renderCollisionCollidersDebug();
    } else this._clearDebugColliders();

    if (this._scene.game.model.showDebugCollisionMap) {
      this._renderCollisionGridDebug();
    } else this._clearDebugMap();
  }

  _clearDebugColliders() {
    if (this._debugColliders) {
      this._debugColliders.destroy();
      this._debugColliders = null;
    }
  }
  _clearDebugMap() {
    if (this._debugMap) {
      this._debugMap.destroy();
      this._debugMap = null;
    }
  }

  _renderCollisionCollidersDebug() {
    if (!this._debugColliders) this._debugColliders = this._scene.add.graphics();
    this._debugColliders.clear();
    for (let i = 0; i < this._collisionColliders.length; i++) {
      const collider = this._collisionColliders[i];
      if (collider.enabled) {
        this._debugColliders.lineStyle(2, collider.debugColour);
        this._debugColliders.fillStyle(collider.debugColour, 0.1);
        this._debugColliders.strokeRect(collider.x, collider.y, collider.width, collider.height);
        this._debugColliders.fillRect(collider.x, collider.y, collider.width, collider.height);
      }
    }
  }

  _renderCollisionGridDebug() {
    if (this._debugMap) this._debugMap.clear();
    else this._debugMap = this._scene.add.graphics();

    for (let y = 0; y < this._collisionGrid.length; y++) {
      for (let x = 0; x < this._collisionGrid[y].length; x++) {
        const pos = this.gridToWorld(x, y, true);
        const colour = this._collisionGrid[y][x] === 0 ? 0x00ff00 : 0xff0000;
        this._debugMap.fillStyle(colour, 0.2);
        this._debugMap.fillRect(pos.x - 5, pos.y - 5, 10, 10);
      }
    }
  }
}
